'use client'

import { useState, useRef } from 'react'
import Image from 'next/image'
import { getReadableImageUrl } from '@/lib/directus'

// Image imports
import Logo from '/public/images/logo_h_new.png'

interface HomeSldOfficeProps {
  data?: {
    heading?: {
      title: string
    }
    images?: {
      images_id: {
        alt: string
        image_file: {
          image: {
            id: string
            filename_download: string
          }
        }
      }
    }[]
  }
}

export const fields = [
  'heading.title',
  'images.images_id.alt',
  'images.images_id.image_file.image.id',
  'images.images_id.image_file.image.filename_download',
]

export default function HomeSldOffice({ data }: HomeSldOfficeProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [dragStartTime, setDragStartTime] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [thumbnailsOffset, setThumbnailsOffset] = useState(0)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const [visibleThumbnails, setVisibleThumbnails] = useState({ desktop: 3.5, mobile: 2 })

  const officeImages = data?.images?.map(img => getReadableImageUrl(img.images_id.image_file.image.id, '')) || ['']
  const thumbnailImages = [...officeImages]

  // Вычисляем максимальное смещение для thumbnails
  const calculateMaxOffset = (isMobile = false) => {
    const thumbnailWidth = isMobile ? 140 : 220
    const gap = isMobile ? 10 : 17
    const visibleCount = isMobile ? visibleThumbnails.mobile : visibleThumbnails.desktop
    
    return -Math.max(0, (thumbnailImages.length - visibleCount) * (thumbnailWidth + gap))
  }

  // Fade animation for main image
  const handleImageTransition = (newIndex: number) => {
    setIsTransitioning(true)
    setTimeout(() => {
      setCurrentImageIndex(newIndex)
      
      const isMobile = window.innerWidth < 768
      const thumbnailWidth = isMobile ? 140 : 220
      const gap = isMobile ? 10 : 17
      const slideWidth = thumbnailWidth + gap
      
      let newOffset = -(newIndex * slideWidth)
      const maxOffset = calculateMaxOffset(isMobile)
      
      if (newOffset < maxOffset) {
        newOffset = maxOffset
      }
      
      setThumbnailsOffset(Math.max(newOffset, maxOffset))
      
      setTimeout(() => {
        setIsTransitioning(false)
      }, 300)
    }, 300)
  }

  const handlePrevClick = () => {
    const newIndex = currentImageIndex === 0 ? officeImages.length - 1 : currentImageIndex - 1
    handleImageTransition(newIndex)
  }

  const handleNextClick = () => {
    const newIndex = currentImageIndex === officeImages.length - 1 ? 0 : currentImageIndex + 1
    handleImageTransition(newIndex)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return
    setIsDragging(true)
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft)
    setScrollLeft(scrollContainerRef.current.scrollLeft)
    setDragStartTime(Date.now())
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return
    e.preventDefault()
    const x = e.pageX - scrollContainerRef.current.offsetLeft
    const walk = (x - startX)
    
    const newOffset = thumbnailsOffset + walk
    const maxOffset = calculateMaxOffset()
    
    if (newOffset <= 0 && newOffset >= maxOffset) {
      setThumbnailsOffset(newOffset)
    }
  }

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging) return

    const dragEndTime = Date.now()
    const dragDuration = dragEndTime - dragStartTime

    if (dragDuration < 200) {
      const clickedElement = e.target as HTMLElement
      const thumbnailButton = clickedElement.closest('button')
      if (thumbnailButton) {
        const index = Number(thumbnailButton.getAttribute('data-index'))
        if (!isNaN(index)) {
          handleImageTransition(index)
        }
      }
    } else {
      const thumbnailWidth = 220
      const gap = 17
      const slideWidth = thumbnailWidth + gap
      
      const nearestSlide = Math.round(Math.abs(thumbnailsOffset) / slideWidth)
      
      let newOffset = -(nearestSlide * slideWidth)
      const maxOffset = calculateMaxOffset()
      
      if (newOffset < maxOffset) {
        newOffset = maxOffset
      }
      
      setThumbnailsOffset(Math.max(newOffset, maxOffset))
      setCurrentImageIndex(nearestSlide)
    }

    setIsDragging(false)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
    setStartX(e.targetTouches[0].clientX)
    setDragStartTime(Date.now())
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
    
    if (!scrollContainerRef.current) return
    const x = e.targetTouches[0].clientX
    const walk = (x - startX)
    
    const isMobile = window.innerWidth < 768
    
    const newOffset = thumbnailsOffset + walk
    const maxOffset = calculateMaxOffset(isMobile)
    
    if (newOffset <= 0 && newOffset >= maxOffset) {
      setThumbnailsOffset(newOffset)
    }
  }

  const handleTouchEnd = () => {
    const dragEndTime = Date.now()
    const dragDuration = dragEndTime - dragStartTime

    if (dragDuration < 200 && Math.abs(touchStart - touchEnd) < 10) {
      const clickedElement = document.elementFromPoint(touchEnd, scrollContainerRef.current?.getBoundingClientRect().top || 0)
      const thumbnailButton = clickedElement?.closest('button')
      if (thumbnailButton) {
        const index = Number(thumbnailButton.getAttribute('data-index'))
        if (!isNaN(index)) {
          handleImageTransition(index)
        }
      }
    } else {
      const isMobile = window.innerWidth < 768
      const thumbnailWidth = isMobile ? 140 : 220
      const gap = isMobile ? 10 : 17
      const slideWidth = thumbnailWidth + gap
      
      const swipeDistance = touchStart - touchEnd
      if (Math.abs(swipeDistance) > 50) {
        if (swipeDistance > 0) {
          const nextSlide = Math.min(
            currentImageIndex + 1,
            officeImages.length - 1
          )
          handleImageTransition(nextSlide)
        } else {
          const prevSlide = Math.max(currentImageIndex - 1, 0)
          handleImageTransition(prevSlide)
        }
      } else {
        const nearestSlide = Math.round(Math.abs(thumbnailsOffset) / slideWidth)
        
        let newOffset = -(nearestSlide * slideWidth)
        const maxOffset = calculateMaxOffset(isMobile)
        
        if (newOffset < maxOffset) {
          newOffset = maxOffset
        }
        
        setThumbnailsOffset(Math.max(newOffset, maxOffset))
        setCurrentImageIndex(Math.min(nearestSlide, officeImages.length - 1))
      }
    }

    setTouchStart(0)
    setTouchEnd(0)
  }

  return (
    <>
      <section className='relative overflow-hidden py-[3rem] lg:hidden block'>
        <div className='container relative'>
          <div className='space-y-4'>
            <h2 className='font-agopus text-4xl text-black'>{data?.heading?.title || 'Office'}</h2>
          </div>
        </div>
      </section>
      <section className='HomeSldOffice relative w-screen lg:h-screen h-[320px] lg:overflow-hidden bg-white lg:block max-[460px]:mb-[85px]'>
        <div className='relative w-full h-full max-md:rounded-tl-[50px] overflow-hidden'>
          <div
            className={`absolute inset-0 transition-opacity duration-300 ease-in-out 
            ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
          >
            <Image
              src={officeImages[currentImageIndex]}
              alt={data?.images?.[currentImageIndex]?.images_id?.alt || ''}
              fill
              className='object-cover object-center'
              priority
              quality={90}
            />
          </div>
        </div>

        <div className='absolute md:left-[8.688rem] md:top-[7.563rem] right-4 top-[-60px] md:right-auto'>
          <div className='md:p-5 p-3'>
            <div className='relative w-[5rem] h-[5rem] md:w-[6rem] md:h-[6rem]'>
              <Image
                src={Logo}
                alt='Harmon logo'
                fill
                className='object-contain'
              />
            </div>
          </div>
        </div>

        <div className='absolute right-0 md:bottom-[8rem] bottom-16 max-[460px]:relative max-[460px]:bottom-0'>
          <div className='bg-green-light/80 backdrop-blur-[39.2px] md:rounded-l-[0.625rem] md:py-6 md:pl-6 py-3 pl-6'>
            <div className='md:w-[calc(220px*3.5+17px*3)] w-full overflow-hidden'>
              <div
                ref={scrollContainerRef}
                className='flex md:gap-[1.063rem] gap-2 no-scrollbar touch-pan-x'
                style={{
                  transform: `translateX(${thumbnailsOffset}px)`,
                  transition: isDragging || touchStart !== 0 ? 'none' : 'transform 500ms ease-in-out',
                  cursor: isDragging ? 'grabbing' : 'grab',
                  userSelect: 'none',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {thumbnailImages.map((image, index) => (
                  <button
                    key={index}
                    data-index={index}
                    className={`relative shrink-0 md:w-[13.75rem] md:h-[8.25rem] w-[6.25rem] h-[3.75rem] 
                    rounded-[0.625rem] overflow-hidden transition-all duration-500 ease-in-out
                    ${currentImageIndex === index ? 'border-2 border-brown' : ''}`}
                  >
                    <Image
                      src={image}
                      alt={data?.images?.[index]?.images_id?.alt || ''}
                      fill
                      className='object-cover pointer-events-none'
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className='absolute left-1/2 -translate-x-1/2 bottom-7 flex flex-col items-center'>
          <div className='flex items-center gap-8 md:gap-[11.25rem]'>
            <button
              onClick={handlePrevClick}
              className='group w-[18px] h-[28px] flex items-center justify-center'
              aria-label='Previous image'
            >
              <svg
                width='9'
                height='20'
                viewBox='0 0 9 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M7.83957 0.725224L0.209024 9.42708C0.0750055 9.57992 0 9.78492 0 9.99836C0 10.2118 0.0750055 10.4168 0.209024 10.5696L7.83957 19.2748C7.90199 19.3461 7.97664 19.4027 8.05908 19.4414C8.14153 19.4801 8.23011 19.5 8.3196 19.5C8.40909 19.5 8.49768 19.4801 8.58012 19.4414C8.66257 19.4027 8.73721 19.3461 8.79964 19.2748C8.92809 19.1286 9 18.9324 9 18.728C9 18.5237 8.92809 18.3275 8.79964 18.1813L1.62411 9.99836L8.79964 1.81705C8.92765 1.67096 8.99928 1.47508 8.99928 1.27114C8.99928 1.0672 8.92765 0.871316 8.79964 0.725224C8.73721 0.653947 8.66257 0.597295 8.58012 0.558612C8.49768 0.519929 8.40909 0.5 8.3196 0.5C8.23011 0.5 8.14153 0.519929 8.05908 0.558612C7.97664 0.597295 7.90199 0.653947 7.83957 0.725224Z'
                  className='fill-white group-hover:fill-brown transition-colors'
                />
              </svg>
            </button>

            <button
              onClick={handleNextClick}
              className='group w-[18px] h-[28px] flex items-center justify-center'
              aria-label='Next image'
            >
              <svg
                width='9'
                height='20'
                viewBox='0 0 9 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M1.16043 0.725224L8.79098 9.42708C8.92499 9.57992 9 9.78492 9 9.99836C9 10.2118 8.92499 10.4168 8.79098 10.5696L1.16043 19.2748C1.09801 19.3461 1.02336 19.4027 0.940917 19.4414C0.858469 19.4801 0.769887 19.5 0.680397 19.5C0.590907 19.5 0.502323 19.4801 0.419875 19.4414C0.337428 19.4027 0.262787 19.3461 0.200358 19.2748C0.0719118 19.1286 9.53674e-07 18.9324 9.53674e-07 18.728C9.53674e-07 18.5237 0.0719118 18.3275 0.200358 18.1813L7.37589 9.99836L0.200358 1.81705C0.0723524 1.67096 0.000720024 1.47508 0.000720024 1.27114C0.000720024 1.0672 0.0723524 0.871316 0.200358 0.725224C0.262787 0.653947 0.337428 0.597295 0.419875 0.558612C0.502323 0.519929 0.590907 0.5 0.680397 0.5C0.769887 0.5 0.858469 0.519929 0.940917 0.558612C1.02336 0.597295 1.09801 0.653947 1.16043 0.725224Z'
                  className='fill-white group-hover:fill-brown transition-colors'
                />
              </svg>
            </button>
          </div>
        </div>
      </section>
    </>
  )
}